// Email verification: the screen a new developer sees until they open the
// link we emailed, and the page that link opens (/verify-email?token=…).
import { useEffect, useRef, useState } from "react";
import { Button, ErrorNotice } from "../components/ui";
import { ApiClient, ApiError, authApi, defaultBaseUrl } from "../lib/api";
import { navigate, useLocation } from "../lib/router";
import { useSession } from "../lib/session";
import { AuthLayout } from "./Login";

function resendMessage(err: unknown): string | null {
  if (err instanceof ApiError && err.status === 429) return "We just sent one. You can ask for another in a minute.";
  return null;
}

/** Shown instead of the console while the developer's email isn't verified. */
export function CheckEmail() {
  const { session, client, signOut, refreshVerification } = useSession();
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState<"resend" | "check" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notYet, setNotYet] = useState(false);
  const failedAtSignUp = session?.verificationEmailSent === false;

  const resend = async () => {
    if (!client) return;
    setBusy("resend");
    setError(null);
    setNotYet(false);
    try {
      const r = await authApi(client).resendVerification();
      setSent(r.sent_to);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const check = async () => {
    setBusy("check");
    setError(null);
    try {
      const ok = await refreshVerification();
      if (ok) navigate("/dev/games?welcome=1", { replace: true });
      else setNotYet(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const limited = resendMessage(error);
  return (
    <AuthLayout>
      <div className="verify-card" role="status" aria-live="polite">
        <div className="verify-icon" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 6.5 8.5 6.5 8.5-6.5" />
          </svg>
        </div>
        <h2>Check your email</h2>
        <p className="muted">
          {failedAtSignUp && !sent
            ? <>Your account is ready, but we couldn't send the verification email to <strong>{session?.email}</strong>. Send it again below.</>
            : <>We sent a link to <strong>{sent ?? session?.email}</strong>. Open it to verify your email and start building. The link works for 24 hours.</>}
        </p>
        {sent && <div className="notice role">A new link is on its way to {sent}.</div>}
        {notYet && <div className="notice warn">Not verified yet. Open the link in the email, then try again.</div>}
        {limited ? <div className="notice warn">{limited}</div> : <ErrorNotice error={error} />}
        <div className="verify-actions">
          <Button variant="primary" busy={busy === "check"} onClick={check}>I've verified my email</Button>
          <Button busy={busy === "resend"} onClick={resend}>Resend email</Button>
        </div>
        <p className="muted small">
          Can't find it? Check your spam folder, or{" "}
          <button type="button" className="link-button" onClick={() => signOut()}>sign out</button> and sign up with another address.
        </p>
      </div>
    </AuthLayout>
  );
}

type State = { kind: "working" } | { kind: "done"; email: string } | { kind: "failed"; error: unknown };

/** The page the verification link opens. Works signed in or not. */
export function VerifyEmailPage() {
  const { search } = useLocation();
  const { session, refreshVerification } = useSession();
  const token = new URLSearchParams(search).get("token") ?? "";
  const [state, setState] = useState<State>(token ? { kind: "working" } : { kind: "failed", error: new ApiError(400, "verification_token_invalid", "This link is missing its token. Open the link from the email again.") });
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    authApi(new ApiClient(defaultBaseUrl(), ""))
      .verifyEmail(token)
      .then(async (r) => {
        setState({ kind: "done", email: r.developer.contact_email });
        if (session?.role === "developer" && session.developerId === r.developer.id) await refreshVerification().catch(() => false);
      })
      .catch((error) => setState({ kind: "failed", error }));
    // The token is read once; the session can change underneath without re-running this.
  }, [token]);

  const signedIn = session?.role === "developer";
  const failure = state.kind === "failed" && state.error instanceof ApiError ? state.error.code : "";
  const expired = failure === "verification_token_expired";
  // Anything other than a bad or expired link (no connection, a server error) means we couldn't check it.
  const unchecked = state.kind === "failed" && !expired && failure !== "verification_token_invalid";
  return (
    <AuthLayout>
      <div className="verify-card" role="status" aria-live="polite">
        {state.kind === "working" && (
          <>
            <h2>Verifying your email…</h2>
            <p className="muted">This takes a second.</p>
          </>
        )}
        {state.kind === "done" && (
          <>
            <div className="verify-icon ok" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
            </div>
            <h2>Email verified</h2>
            <p className="muted"><strong>{state.email}</strong> is confirmed. Your sandbox is ready.</p>
            <div className="verify-actions">
              {signedIn
                ? <Button variant="primary" onClick={() => navigate("/dev/games?welcome=1", { replace: true })}>Continue to your console</Button>
                : <Button variant="primary" onClick={() => navigate("/login", { replace: true })}>Sign in</Button>}
            </div>
          </>
        )}
        {state.kind === "failed" && (
          <>
            <h2>{expired ? "This link has expired" : unchecked ? "We couldn't check your link" : "This link didn't work"}</h2>
            <p className="muted">
              {expired
                ? "Verification links work for 24 hours. Sign in and we'll send you a new one."
                : unchecked
                  ? "Open the link from the email again in a moment."
                  : "It may have been replaced by a newer email, or copied incompletely. Sign in to get a new link."}
            </p>
            {unchecked && <ErrorNotice error={state.error} />}
            <div className="verify-actions">
              <Button variant="primary" onClick={() => navigate(signedIn ? "/dev" : "/login", { replace: true })}>
                {signedIn ? "Back to the console" : "Sign in"}
              </Button>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
